import type { TeamMembership } from '@rhythmic/db/types'
import type { UpdateTeamMembershipPayload } from '@rhythmic/types'
import { UpdateTeamMembershipSchema } from '@rhythmic/types'
import { useRhythmicLogger } from '#imports'

import { getTeamMembershipCollection } from '../../getCollections'

const logger = useRhythmicLogger('useTeamContext:updateTeamMembership')

export interface UpdateTeamMembershipContext {
  teamId: string
}

export interface UpdateTeamMembershipParams {
  userId: string
  changes: UpdateTeamMembershipPayload
}

function validateParams(params: UpdateTeamMembershipParams, context: UpdateTeamMembershipContext) {
  const { userId, changes } = params
  const { teamId } = context

  if (!teamId) {
    const error = new Error('Team context is not ready')
    logger.error('Unable to update team membership: missing team identifier')
    throw error
  }

  if (!userId) {
    const error = new Error('User identifier is required to update team membership')
    logger.error('Unable to update team membership: missing user identifier')
    throw error
  }

  const parsedChanges = UpdateTeamMembershipSchema.parse(changes)

  const applicable = Object.fromEntries(
    Object.entries(parsedChanges).filter(([, value]) => value !== undefined),
  ) as Partial<UpdateTeamMembershipPayload>

  if (Object.keys(applicable).length === 0) {
    const error = new Error('No changes provided to update team membership')
    logger.warn('Team membership update aborted: no changes detected', {
      teamId,
      userId,
    })
    throw error
  }

  return {
    teamId,
    userId,
    changes: applicable,
  }
}

export default async function handleUpdateTeamMembership(
  params: UpdateTeamMembershipParams,
  context: UpdateTeamMembershipContext,
): Promise<TeamMembership | null> {
  try {
    const { teamId, userId, changes } = validateParams(params, context)
    const collection = getTeamMembershipCollection(teamId)

    const result = await collection.update(userId, (draft: TeamMembership & { teamId?: string; userId?: string }) => {
      if (changes.permissions !== undefined) draft.permissions = changes.permissions
      draft.updated_at = new Date()
      draft.teamId = teamId
      draft.userId = userId
    })

    const updated = Array.isArray(result) ? result[0] : result

    logger.info('Team membership updated via collection', { teamId, userId })

    return updated ?? null
  } catch (error) {
    logger.error('updateTeamMembership failed', { params, error })
    if (error instanceof Error) throw error
    throw new Error('Failed to update team membership')
  }
}
